import { TFunction } from 'react-i18next';
import { TableCell, TableColumnType, TableRow } from 'components/Table';
import CheckBox from 'components/CheckBox';
import { Button } from 'components';
import { AutoTradeModelEnum, IAutoTradeModelConfigParams, NOTIFICATION_TABLE_REFS } from '../types';
import { NotificationPreferencesType } from '../services/getNotificationPreferencesApi';

export const InitialiseNotificationTable = (
  translation: TFunction,
  onCheck: (categoryId: string, email: boolean) => void,
) => {
  const NOTIFICATION_TABLE_TEXTS = {
    [NOTIFICATION_TABLE_REFS.ID]: 'id',
    [NOTIFICATION_TABLE_REFS.NOTIFICATION_TYPE]: translation(
      `account:settings.table.headers.${NOTIFICATION_TABLE_REFS.NOTIFICATION_TYPE}`,
    ),
    [NOTIFICATION_TABLE_REFS.EMAIL]: translation(`account:settings.table.headers.${NOTIFICATION_TABLE_REFS.EMAIL}`),
  };

  const notificationColumns: TableColumnType[] = [
    {
      dataRef: NOTIFICATION_TABLE_REFS.NOTIFICATION_TYPE,
      className: '',
      text: NOTIFICATION_TABLE_TEXTS[NOTIFICATION_TABLE_REFS.NOTIFICATION_TYPE],
      cellClassName: 'text-black py-3 border-none',
      cellContentTemplate: (cell: TableCell) => <span className="px-2">{cell.text}</span>,
    },
    {
      dataRef: NOTIFICATION_TABLE_REFS.EMAIL,
      className: 'text-right',
      text: NOTIFICATION_TABLE_TEXTS[NOTIFICATION_TABLE_REFS.EMAIL],
      cellClassName: 'text-black py-3 border-none',
      cellContentTemplate: (cell: TableCell, t, rowData: TableRow['rowData']) => {
        const preference = rowData as unknown as NotificationPreferencesType;
        return (
          <div className="flex justify-end">
            <CheckBox
              inputClassName="max-h-[18px] max-w-[18px] min-h-[18px] min-w-[18px]"
              isChecked={Boolean(cell.text)}
              id={`${preference.categoryId}`}
              onChange={(_, isChecked) => onCheck(preference.categoryId, isChecked)}
            />
          </div>
        );
      },
    },
    {
      dataRef: NOTIFICATION_TABLE_REFS.ID,
      text: NOTIFICATION_TABLE_REFS.ID,
      isVisible: false,
    },
  ];

  return {
    notificationColumns,
  };
};

export const autoTradeModelConfig = ({ t, isLoading, toggleAutoTrade, onClose }: IAutoTradeModelConfigParams) => {
  const config = [
    {
      id: AutoTradeModelEnum.CONFIRM_ENABLE,
      titleSection: (
        <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.model.confirmEnable.title')}</h1>
      ),
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:autoTradeSettings.model.confirmEnable.subText')}</p>
            <ul className="list-disc list-inside">
              {[
                'common:autoTradeSettings.model.confirmEnable.point1',
                'common:autoTradeSettings.model.confirmEnable.point2',
                'common:autoTradeSettings.model.confirmEnable.point3',
              ].map((text, index) => {
                return <li key={index}>{t(text)}</li>;
              })}
            </ul>

            <p>{t('common:autoTradeSettings.model.confirmEnable.endText')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[380px]">
            <Button
              isProcessing={isLoading}
              onClick={() => {
                toggleAutoTrade(true, AutoTradeModelEnum.ENABLED);
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.confirm')}
            </Button>
            <Button
              isDisable={isLoading}
              onClick={() => {
                onClose?.();
              }}
              type="button"
              className="btn-accent bg-white border border-gray-500"
            >
              {t('common:autoTradeSettings.cancel')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: AutoTradeModelEnum.ENABLED,
      titleSection: <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.model.enabled.title')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:autoTradeSettings.model.enabled.subText')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[200px]">
            <Button
              isProcessing={isLoading}
              onClick={() => {
                onClose?.();
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.gotIt')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: AutoTradeModelEnum.CONFIRM_DISABLE,
      titleSection: (
        <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.model.confirmDisable.title')}</h1>
      ),
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:autoTradeSettings.model.confirmDisable.subText')}</p>

            <b className="text-sm">{t('common:autoTradeSettings.model.confirmDisable.subText1')}</b>

            <ul className="list-disc list-inside">
              {[
                'common:autoTradeSettings.model.confirmDisable.point1',
                'common:autoTradeSettings.model.confirmDisable.point2',
                'common:autoTradeSettings.model.confirmDisable.point3',
              ].map((text, index) => {
                return <li key={index}>{t(text)}</li>;
              })}
            </ul>

            <p>{t('common:autoTradeSettings.model.confirmDisable.endText')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[380px]">
            <Button
              isProcessing={isLoading}
              onClick={() => {
                toggleAutoTrade(false, AutoTradeModelEnum.DISABLED);
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.sendRequest')}
            </Button>
            <Button
              isDisable={isLoading}
              onClick={() => {
                onClose?.();
              }}
              type="button"
              className="btn-accent bg-white border border-gray-500"
            >
              {t('common:autoTradeSettings.cancel')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: AutoTradeModelEnum.DISABLED,
      titleSection: <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.model.disabled.title')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p>{t('common:autoTradeSettings.model.disabled.subText')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[200px]">
            <Button
              isProcessing={isLoading}
              onClick={() => {
                onClose?.();
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.gotIt')}
            </Button>
          </div>
        </div>
      ),
    },

    {
      id: AutoTradeModelEnum.ERROR,
      titleSection: <h1 className="text-center px-2 mb-2">{t('common:autoTradeSettings.error')}</h1>,
      textSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-col gap-4 text-sm w-full max-w-[400px]">
            <p className="text-center">{t('common:autoTradeSettings.errorMessage')}</p>
          </div>
        </div>
      ),
      buttonSection: (
        <div className="flex flex-col items-center ">
          <div className="flex flex-row gap-2 items-center justify-center w-full max-w-[200px]">
            <Button
              isProcessing={isLoading}
              onClick={() => {
                onClose?.();
              }}
              type="button"
              className="btn-accent"
            >
              {t('common:autoTradeSettings.close')}
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return config;
};
